import type { FileUIPart } from 'ai';

// Attachment processing for the chat composer. Attachments travel as AI SDK
// `file` UI parts (data URLs), which `convertToModelMessages` on the chat API
// turns into multimodal content for the model — see the AI SDK multimodal
// chatbot guide. The chat API externalizes the bytes to a private GCS bucket
// on persist and returns signed read URLs when history is reloaded. Large
// images are downscaled client-side so the request payload stays small.

// Mirrors the server-side caps in crm-chat-api/routes/chat.ts so oversized
// attachments are rejected before a request is made. HEIC/HEIF (iPhone
// photos) is accepted too: re-encoded to JPEG here when the browser decodes
// it (Safari), converted by the chat API otherwise. Some browsers give a
// .heic file no type, hence the extensions.
// CSV and Excel files are read by the chat's code sandbox, not by the model.
export const ACCEPTED_FILE_TYPES =
  'image/png,image/jpeg,image/webp,image/gif,image/heic,image/heif,.heic,.heif,application/pdf,' +
  'text/csv,application/vnd.ms-excel,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const ACCEPTED_MEDIA_TYPES = new Set(
  ACCEPTED_FILE_TYPES.split(',').filter(type => type.includes('/')),
);

export const isAcceptedFile = (file: { type: string; name: string }) =>
  ACCEPTED_MEDIA_TYPES.has(file.type) || /\.hei[cf]$/i.test(file.name);

// Image types no chat model reads. When the browser can decode one it is
// always re-encoded to JPEG, whatever its size.
const NON_MODEL_IMAGE_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/avif',
]);

const ascii = (bytes: Uint8Array, start: number, end: number): string =>
  String.fromCharCode(...bytes.subarray(start, end));

const HEIC_BRANDS = new Set([
  'heic',
  'heix',
  'hevc',
  'hevx',
  'heim',
  'heis',
  'hevm',
  'hevs',
]);

// ISO-BMFF ftyp brands (major + compatible) within the first bytes.
const ftypBrands = (head: Uint8Array): string[] => {
  if (head.length < 16 || ascii(head, 4, 8) !== 'ftyp') {
    return [];
  }
  const brands = [ascii(head, 8, 12)];
  for (let offset = 16; offset + 4 <= head.length; offset += 4) {
    brands.push(ascii(head, offset, offset + 4));
  }
  return brands;
};

/**
 * The image type from the first bytes of a file, or null. An iPhone photo
 * can be named IMG_1234.jpg (and typed image/jpeg by the browser) while being
 * HEIC; the bytes are the only reliable source. Mirrors
 * `sniffUploadedImageType` in crm-chat-api/lib/attachments.ts.
 */
export const sniffImageType = (head: Uint8Array): string | null => {
  if (head[0] === 0x89 && ascii(head, 1, 4) === 'PNG') {
    return 'image/png';
  }
  if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) {
    return 'image/jpeg';
  }
  if (ascii(head, 0, 4) === 'RIFF' && ascii(head, 8, 12) === 'WEBP') {
    return 'image/webp';
  }
  if (/^GIF8[79]a$/.test(ascii(head, 0, 6))) {
    return 'image/gif';
  }
  const brands = ftypBrands(head);
  if (HEIC_BRANDS.has(brands[0] ?? '')) {
    return 'image/heic';
  }
  if (brands.some(brand => brand === 'avif' || brand === 'avis')) {
    return 'image/avif';
  }
  if (brands.some(brand => HEIC_BRANDS.has(brand))) {
    return 'image/heic';
  }
  if (brands.some(brand => brand === 'mif1' || brand === 'msf1')) {
    return 'image/heif';
  }
  return null;
};

// Enough for the signatures above plus a few compatible ftyp brands.
const SNIFF_BYTES = 64;

export const realFileType = async (file: File): Promise<string> =>
  sniffImageType(
    new Uint8Array(await file.slice(0, SNIFF_BYTES).arrayBuffer()),
  ) ?? file.type;

const withDataUrlType = (url: string, mediaType: string): string =>
  `data:${mediaType};base64,${url.slice(url.indexOf(',') + 1)}`;
export const MAX_FILES_PER_MESSAGE = 5;
// Server cap on the total data-URL characters of a message (~7.5MB decoded).
export const MAX_TOTAL_FILE_CHARS = 10_000_000;

// Images at or below both limits are attached as-is. Larger ones are
// downscaled and re-encoded as JPEG so the JSON payload and persisted rows stay
// small. The model is sent every photo of the conversation on each turn, and
// Anthropic rejects a request with more than 20 images when any of them has a
// side above 2000 px.
const PASSTHROUGH_BYTES = 1024 * 1024;
const MAX_DIMENSION = 2000;
const JPEG_QUALITY = 0.85;

// Untouched originals of resized photos are uploaded next to the resized copy
// so they can be saved to properties and downloaded at full quality. Mirrors
// the server's accepted types and size cap for originals.
const ORIGINAL_MEDIA_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_ORIGINAL_BYTES = 15 * 1024 * 1024;

export type ChatFileUIPart = FileUIPart & { attachmentId: string };

export type ProcessedAttachment = {
  part: ChatFileUIPart;
  // Set only when `part` is a resized copy of an original worth keeping.
  original?: File;
};

type ImageDimensions = { width: number; height: number };

export const shouldDownscale = ({
  width,
  height,
  size,
}: ImageDimensions & { size: number }): boolean =>
  Math.max(width, height) > MAX_DIMENSION || size > PASSTHROUGH_BYTES;

export const downscaledDimensions = ({
  width,
  height,
}: ImageDimensions): ImageDimensions => {
  const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

export const shouldKeepOriginal = ({
  type,
  size,
}: {
  type: string;
  size: number;
}): boolean => ORIGINAL_MEDIA_TYPES.has(type) && size <= MAX_ORIGINAL_BYTES;

const readAsDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const bitmapToJpegDataUrl = (bitmap: ImageBitmap): string => {
  const canvas = document.createElement('canvas');
  const { width, height } = downscaledDimensions(bitmap);
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2d context unavailable');
  }
  // JPEG has no alpha channel — flatten transparency onto white instead of
  // letting it turn black.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
};

// Null when the image needs no resizing. `force` re-encodes it anyway (a
// format the model cannot read).
const downscaleToJpegDataUrl = async (
  file: File,
  force: boolean,
): Promise<string | null> => {
  const bitmap = await createImageBitmap(file);
  try {
    if (
      !force &&
      !shouldDownscale({
        width: bitmap.width,
        height: bitmap.height,
        size: file.size,
      })
    ) {
      return null;
    }
    return bitmapToJpegDataUrl(bitmap);
  } finally {
    bitmap.close();
  }
};

const attachmentIdFromDataUrl = async (url: string): Promise<string> => {
  const base64 = url.slice(url.indexOf(',') + 1);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), byte =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
};

// Converts a picked/pasted file into a sendable file part, or null when it
// cannot be made to fit the server's limits. The type comes from the bytes.
// Images above the passthrough size or dimension, and HEIC/HEIF/AVIF of any
// size, are re-encoded to JPEG; when decoding fails (e.g. HEIC outside
// Safari) the raw data URL is used, labelled with its real type so the chat
// API converts it, and only if the RESULTING string fits the server's cap —
// base64 is ~1.37x the file size, so gating on file bytes alone would let
// through payloads the server then 400s.
export const fileToFileUIPart = async (
  file: File,
): Promise<ProcessedAttachment | null> => {
  const filename = file.name || undefined;
  const type = await realFileType(file);

  if (type.startsWith('image/')) {
    try {
      const url = await downscaleToJpegDataUrl(
        file,
        NON_MODEL_IMAGE_TYPES.has(type),
      );
      if (url != null) {
        return {
          part: {
            type: 'file',
            filename,
            url,
            mediaType: 'image/jpeg',
            attachmentId: await attachmentIdFromDataUrl(url),
          },
          original: shouldKeepOriginal({ type, size: file.size })
            ? file
            : undefined,
        };
      }
    } catch {
      // Fall through to the raw read below.
    }
  }

  const url = withDataUrlType(await readAsDataUrl(file), type);
  if (url.length > MAX_TOTAL_FILE_CHARS) {
    return null;
  }
  return {
    part: {
      type: 'file',
      filename,
      url,
      mediaType: type,
      attachmentId: await attachmentIdFromDataUrl(url),
    },
  };
};

// Errors a new chat can fix: the conversation's accumulated photos no longer
// fit one model request (413 from the gateway, Anthropic's many-image
// dimension limit). Not the per-message attachment cap, which a new chat would
// not change.
const TOO_MANY_PHOTOS_ERROR =
  /entity too large|\b413\b|many-image|image dimensions exceed/i;

export const isTooManyPhotosError = (message: string): boolean =>
  TOO_MANY_PHOTOS_ERROR.test(message);

// Client-side guard mirroring the server's attachment limits. Returns an error
// message key to display (via t()), or null when the list is acceptable.
export const validateAttachments = (
  current: ChatFileUIPart[],
  t: (key: string) => string,
): string | null => {
  if (current.length > MAX_FILES_PER_MESSAGE) {
    return t('You can attach at most 5 files per message.');
  }
  const totalChars = current.reduce((sum, file) => sum + file.url.length, 0);
  if (totalChars > MAX_TOTAL_FILE_CHARS) {
    return t('Attachments are too large. Keep the total under ~7MB.');
  }
  return null;
};
